import { RoutesService, ConfigStateService, ReplaceableComponentsService, AuthService } from '@abp/ng.core';
import { eSettingManagementRouteNames } from '@abp/ng.setting-management';
import { UserMenuService, eThemeSharedRouteNames } from '@abp/ng.theme.shared';
import { Injectable } from '@angular/core';
import { ManageProfileTabsService } from '@volo/abp.ng.account/public/config';
import { eUserMenuItems } from '@volosoft/abp.ng.theme.lepton-x';
import { ThemeService } from '@volosoft/ngx-lepton-x';
import { LogoutServiceService } from '../logout/logout-service.service';
import { ImpersonationService } from '@volo/abp.commercial.ng.ui/config';
import { Router } from '@angular/router';
import { filter, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ConfigMenuService {

  constructor(
    private routes: RoutesService,
    private configState: ConfigStateService,
    private userMenu: UserMenuService,
    private replacableComponents: ReplaceableComponentsService,
    private themeService: ThemeService,
    private tabs: ManageProfileTabsService,
    private authService: AuthService,
    private logoutService: LogoutServiceService,
    private router: Router,
    private impersonation: ImpersonationService,
  ) { }

  openPrivacyPolicies() {
      // Converts the route into a string that can be used 
      // with the window.open() function
      const url = this.router.serializeUrl(
        this.router.createUrlTree([`/privacy-policy`])
      );
  
      window.open(url, '_blank');
    }

  configTenants()
  {
    // Reage ao currentUser AUTENTICADO em vez de ler um snapshot único no boot. Com o login via SPA
    // (sem reload), este serviço não é recriado e o AppComponent não roda o ngOnInit de novo, então a
    // leitura síncrona antiga pegava o usuário anônimo e nunca escondia o menu de administração do
    // tenant (só resolvia após um F5). Esperar isAuthenticated garante o contexto certo do tenant
    // depois do refreshAppState do login.
    this.configState.getOne$("currentUser").pipe(
      filter((currentUser: any) => currentUser?.isAuthenticated === true),
      take(1),
    ).subscribe(currentUser => {
      try {
        const isInHostTenant = currentUser.tenantId == null;

        if (isInHostTenant) {
          if (currentUser.roles?.includes('admin')) {
            this.routes.patch(eThemeSharedRouteNames.Administration, { order: 101, invisible: false });
          }

          else if (currentUser.roles?.includes('Admin Cargo Fleet')) {
            this.routes.patch('::Menu:Dashboard', { invisible: true });
            this.routes.patch('Saas::Editions', { invisible: true });
            this.routes.patch(eSettingManagementRouteNames.Settings, { invisible: true });
            this.routes.patch('Saas::Tenants', { path: "/" });
            this.tabs.remove(["Acesso Galileu"])
          }
        }
        else {
          // Tenant: a gestão de usuários mora na aba "Usuários e Acessos" do Minha conta
          // (decisão do Ronan 14/08) — o menu "Administração" padrão do ABP confundia a
          // transportadora (Idiomas, Modelos de Texto, Configurações...). REMOVE, não patch de
          // invisible: no login SPA (sem reload) o navbar do LeptonX não reflete patch de
          // propriedade feito depois do render — reproduzido no ambiente em 14/08 (o item só
          // sumia após F5). remove/add mudam a ÁRVORE, que é o que o bridge do tema escuta — o
          // mesmo mecanismo dos menus por feature, que sempre funcionaram no login. Remover é
          // seguro aqui: cada sessão SPA é só-host ou só-tenant (o logout recarrega a página).
          // As ROTAS continuam protegidas pelas policies — isto é só menu.
          // "Registros de auditoria" SAI junto (Ronan 17/08): quem lê a trilha somos nós, da
          // CargoFleet — a transportadora não precisa de menu de admin. A whitelist de auditoria
          // no backend continua gravando; só a porta de entrada no tenant é que fecha.
          this.routes.remove([
            'AbpIdentity::Menu:IdentityManagement',
            'LanguageManagement::LanguageManagement',
            'TextTemplateManagement::Menu:TextTemplates',
            'AbpAuditLogging::Menu:AuditLogging',
            eSettingManagementRouteNames.Settings,
          ]);
        }
      } catch (error) {
        console.error('Erro ao configurar o menu por tenant:', error);
      }
    });
  }

  configGlobal()
  {
    this.userMenu.removeItem(eUserMenuItems.AuthorityDelegation)
    // "Voltar ao usuário original": o item nativo do ABP é um ícone da barra (NavItemsService) que a
    // toolbar customizada da casa não desenha, e no menu do usuário ele nunca existiu nesta versão.
    // Quem entra num tenant pela tela Clientes do host precisa do caminho de volta.
    //
    // O item entra SEMPRE e quem decide se aparece é o predicado `visible`, avaliado a cada abertura
    // do menu — exatamente o que o item nativo do ABP faz. Decidir uma vez só, por um take(1) sobre
    // o currentUser, congelava a resposta no boot: o item só existia porque impersonate() termina
    // em reloadPage(), e esse reload está dentro de um navigateByUrl().then(...). Se a navegação
    // rejeitasse (guard ou resolver lançando), a página não recarregava e a sessão ficava
    // impersonada sem caminho de volta no menu, restando o logout.
    this.userMenu.addItems([
      {
        id: eUserMenuItems.BackToImpersonator,
        order: 98,
        textTemplate: { icon: 'bi bi-arrow-counterclockwise', text: 'Voltar ao usuário original' },
        visible: () => this.impersonation.isImpersonatorVisible(),
        action: () => this.impersonation.impersonate({}).subscribe(),
      },
    ]);
    this.userMenu.removeItem(eUserMenuItems.LinkedAccounts)
    this.userMenu.removeItem(eUserMenuItems.Sessions)
    this.userMenu.removeItem(eUserMenuItems.Logout)
    this.userMenu.removeItem(eUserMenuItems.MyAccount)
    this.userMenu.removeItem("Gdpr.GdprNavigation")

    this.userMenu.addItems([
      {
        id: eUserMenuItems.MyAccount,
        order: 103,
        textTemplate: {
            icon: 'bi bi-sliders',
            text: 'AbpAccount::MyAccount',
        },
        action: () => this.router.navigate(["/account/manage"]),
    },
    ])

    this.userMenu.addItems([{
      id: 'ApiStatusMenu',
      order: 100,
      textTemplate: {
        icon: "fas fa-wifi",
        text: "Status da API"
      },
      action: () => {
        this.router.navigate(['/api-status'])
      }
    }])

    this.userMenu.addItems([{
      id: 'PrivacyPolicies',
      order: 103,
      textTemplate: {
        icon: "fas fa-lock",
        text: "Política de privacidade"
      },
      action: () => {
        this.openPrivacyPolicies()
      }
    }])

    this.userMenu.addItems([
          {
            id: eUserMenuItems.Logout,
            order: 105,
            textTemplate: {
              icon: 'bi bi-box-arrow-right',
              text: 'Sair',
            },
            action: () => {
              this.logoutService.logout().subscribe({
                next: () => {
                  location.href = "/"
                },
                error: (err) => {
                  location.href = "/"
                },
              });
            },
          },
        ])

  }
}
